import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

/**
 * Composable for locale-aware date formatting
 * Returns dates in German format (dd.MM.yyyy) when locale is 'de'
 * and US format (MM/dd/yyyy) when locale is 'en'
 */
export function useDateFormatter() {
  const { locale } = useI18n();

  const isGermanLocale = computed(() => locale.value === 'de');

  /**
   * Format a date according to the current locale
   * @param timestamp - Unix timestamp in milliseconds or Date object
   * @returns Formatted date string
   */
  const formatDate = (timestamp: number | Date): string => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);

    if (isGermanLocale.value) {
      // German format: dd.MM.yyyy
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      return `${day}.${month}.${year}`;
    } else {
      // English format: MM/dd/yyyy
      return date.toLocaleDateString('en-US');
    }
  };

  /**
   * Format a date with time according to the current locale
   * @param timestamp - Unix timestamp in milliseconds or Date object
   * @returns Formatted date and time string
   */
  const formatDateTime = (timestamp: number | Date): string => {
    const date = timestamp instanceof Date ? timestamp : new Date(timestamp);

    if (isGermanLocale.value) {
      // German format: dd.MM.yyyy, HH:mm
      const day = date.getDate().toString().padStart(2, '0');
      const month = (date.getMonth() + 1).toString().padStart(2, '0');
      const year = date.getFullYear();
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${day}.${month}.${year}, ${hours}:${minutes}`;
    } else {
      // English format: MM/dd/yyyy, h:mm a
      return date.toLocaleDateString('en-US', {
        month: 'numeric',
        day: 'numeric',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
      });
    }
  };

  return {
    formatDate,
    formatDateTime,
    isGermanLocale,
  };
}
